import React, { createContext, useContext, useEffect, useState } from 'react';
import RoleService from 'views/Profile/service/RoleService';
import { rolesStore } from 'views/Roles';

const VerifyRolesContext = createContext();

/**
 * Explicar um pouco da ideia desse contexto e da classe rolesStore.
 * O contexto `VerifyRolesContext` é usado para fornecer as roles do usuário para toda a aplicação,
 * ele só funciona em componentes funcionais, e isso é suficiente para a maioria dos casos.
 * A classe `rolesStore` é uma implementação de um singleton que armazena as roles do usuário,
 * permitindo que outros componentes acessem as roles sem precisar passar o contexto diretamente.
 * Ele é usado em componentes de classe ou em funções que não são componentes React.
 *
 * Ou seja, o `VerifyRolesContext` é usado para fornecer as roles do usuário em componentes funcionais,
 * enquanto a `rolesStore` é usada para acessar as roles em qualquer lugar da aplicação, incluindo componentes de classe e funções que não são componentes React.
 *
 * O evento `rolesLoaded` é disparado quando as roles são carregadas, permitindo que outros componentes ou serviços reajam a essa mudança.
 * Isso é útil para atualizar a interface do usuário ou realizar outras ações quando as roles do usuário são carregadas.
 * Ele é usado nos componentes do tipo classe já que o contexto não funciona nesses componentes e o render não é reativo.
 *
 * os únicos componentes de classe que usam o `rolesStore` (por enquanto) são:
 * src/components/Sidebar/Sidebar.js
 * src/layouts/Admin/Admin.js
 *
 * Todos os outros usam(e devem usar) o `VerifyRolesContext` para acessar as roles do usuário.
 */

export function VerifyRolesProvider({ children }) {
  const [roles, setRoles] = useState(null);

  const fetchRoles = async () => {
    try {
      const userLogin = localStorage.getItem('login');
      if (userLogin) {
        const response = await RoleService.getUserRoles(userLogin);
        setRoles(response.data.data || []);
        rolesStore.setRoles(response.data.data || []);
        window.dispatchEvent(new Event('rolesLoaded'));
      } else {
        setRoles([]);
        rolesStore.setRoles([]);
      }
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
      setRoles([]);
      rolesStore.setRoles([]);
    }
  };

  useEffect(() => {
    fetchRoles();
    const reload = () => fetchRoles();
    window.addEventListener('rolesShouldReload', reload);
    return () => window.removeEventListener('rolesShouldReload', reload);
  }, []);

  return (
    <VerifyRolesContext.Provider
      value={{
        roles
      }}
    >
      {children}
    </VerifyRolesContext.Provider>
  );
}

/**
 *
 * @typedef {Function} VerifyRolesContext
 * @returns {{roles: Array<object>}}
 */
export function useVerifyRolesContext() {
  const context = useContext(VerifyRolesContext);
  if (!context) {
    throw new Error(
      'useVerifyRolesContext must be used within a VerifyRolesContext'
    );
  }
  return context;
}
